import {useEffect, useState} from "react";
import {Button, DialogActions, DialogContent, Switch} from "@mui/material";
import {useMessages} from "i18n";
import BaseDialog from "components/BaseDialog";
import {useConsent} from "consent/useConsent";
import {AlwaysOn, Category, CategoryDesc, CategoryText, CategoryTitle} from "./CookieSettingsDialog.styles";

/**
 * Cookie Settings dialog (ticket ui#72) — opened from the topbar "Resources" menu and from the
 * cookie banner's "Customize" action.
 *
 * Four categories: essential cookies (always on, non-negotiable), analytics cookies, advertising
 * cookies, and — since ui#2331 — the optional "Email me Transferify offers and discounts" choice.
 * The non-essential ones are opt-in and chosen INDEPENDENTLY, because measuring the site, measuring an
 * advertising campaign and being emailed offers are separate purposes with separate legal bases
 * (ui#256, ui#2331). The email row's copy says plainly that it is NOT a cookie: the panel stopped
 * being cookies-only the day this choice joined it, and pretending otherwise would make the notice
 * describe less than the Save button does.
 *
 * All toggles are LOCAL until "Save": Save persists them and marks consent decided (dismissing the
 * banner). Cancel/close discards them. Save answers the email purpose with exactly what the panel
 * shows (owner decision, 2026-09-09: "Saved OFF stops emails") — an off email box records a marketing
 * objection, whether or not the visitor touched that switch, and the row's copy says so. Applying the
 * choice to Google tagging — including tearing a running tag down on withdrawal — belongs to
 * `analytics/useAnalyticsConsent`; carrying the email answer to an account belongs to the marketing
 * replay coordinator. Not to this dialog.
 */

export type CookieSettingsDialogProps = {
	open: boolean;
	onClose: () => void;
	/** Replaces MUI's implicit restore target when the opener disappears while this dialog is open. */
	onTransitionExited?: () => void;
};

export function CookieSettingsDialog({open, onClose, onTransitionExited}: CookieSettingsDialogProps) {
	const {cookieSettings} = useMessages();
	const {consent, savePreferences} = useConsent();
	// Local, unsaved toggles — seeded from the stored choices each time the dialog opens.
	const [analyticsOn, setAnalyticsOn] = useState<boolean>(consent.analytics);
	const [marketingOn, setMarketingOn] = useState<boolean>(consent.marketing);
	const [emailOn, setEmailOn] = useState<boolean>(consent.email === "GRANTED");

	useEffect(() => {
		if (open) {
			setAnalyticsOn(consent.analytics);
			setMarketingOn(consent.marketing);
			setEmailOn(consent.email === "GRANTED");
		}
	}, [open, consent.analytics, consent.marketing, consent.email]);

	const save = () => {
		savePreferences({analytics: analyticsOn, marketing: marketingOn, email: emailOn});
		onClose();
	};

	return (
		<BaseDialog
			title={cookieSettings.title}
			open={open}
			onClose={onClose}
			maxWidth="sm"
			disableRestoreFocus={onTransitionExited !== undefined}
			onTransitionExited={onTransitionExited}
		>
			<DialogContent>
				<Category>
					<CategoryText>
						<CategoryTitle>{cookieSettings.essentialTitle}</CategoryTitle>
						<CategoryDesc>{cookieSettings.essentialDesc}</CategoryDesc>
					</CategoryText>
					<AlwaysOn>{cookieSettings.alwaysOn}</AlwaysOn>
				</Category>
				<Category>
					<CategoryText>
						<CategoryTitle>{cookieSettings.analyticsTitle}</CategoryTitle>
						<CategoryDesc>{cookieSettings.analyticsDesc}</CategoryDesc>
					</CategoryText>
					<Switch
						checked={analyticsOn}
						onChange={(event) => setAnalyticsOn(event.target.checked)}
						slotProps={{input: {"aria-label": cookieSettings.analyticsAria}}}
					/>
				</Category>
				<Category>
					<CategoryText>
						<CategoryTitle>{cookieSettings.marketingTitle}</CategoryTitle>
						<CategoryDesc>{cookieSettings.marketingDesc}</CategoryDesc>
					</CategoryText>
					<Switch
						checked={marketingOn}
						onChange={(event) => setMarketingOn(event.target.checked)}
						slotProps={{input: {"aria-label": cookieSettings.marketingAria}}}
					/>
				</Category>
				<Category>
					<CategoryText>
						<CategoryTitle>{cookieSettings.emailTitle}</CategoryTitle>
						<CategoryDesc>{cookieSettings.emailDesc}</CategoryDesc>
					</CategoryText>
					<Switch
						checked={emailOn}
						onChange={(event) => setEmailOn(event.target.checked)}
						slotProps={{input: {"aria-label": cookieSettings.emailAria}}}
					/>
				</Category>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>{cookieSettings.cancel}</Button>
				<Button variant="contained" onClick={save}>{cookieSettings.save}</Button>
			</DialogActions>
		</BaseDialog>
	);
}
